import { memo } from 'react'
import { Fade, IconButton, Slide, SxProps } from '@mui/material'
import { CrossThinIcon } from '@/assets'

import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'
import { ActionButtons } from '@/components/Inputs'
import { useTranslations } from '@/core/translation'
import { useSlidePanelController } from './SlidePanel.controller'
import { slidePanelStyles as styles } from './SlidePanel.style'
import { SlidePanelProps } from './SlidePanel.types'

function SlidePanel({
  heading,
  subheading,
  children,
  actionButtonProps,
  closeButton,
  open,
  onClose,
  wrapperProps = {},
  contentWrapperProps = {},
  headingContainerProps = {},
  headingTextProps = {},
  iconButtonProps = {},
  mainContentProps = {},
  ...props
}: SlidePanelProps) {
  const t = useTranslations('general')
  const { direction, isModal } = useSlidePanelController({ open, onClose })

  const { sx: wrapperSx, ...otherWrapperProps } = wrapperProps
  const { sx: contentWrapperSx, ...otherContentWrapperProps } = contentWrapperProps
  const { sx: headingContainerSx, ...otherHeadingContainerProps } = headingContainerProps
  const { sx: headingTextSx, ...otherHeadingTextProps } = headingTextProps
  const { sx: iconButtonSx, ...otherIconButtonProps } = iconButtonProps
  const { sx: mainContentSx, ...otherMainContentProps } = mainContentProps

  return (
    <>
      <Fade in={open} mountOnEnter unmountOnExit>
        <Box sx={styles.scrim} onClick={onClose} />
      </Fade>

      <Slide direction={direction} in={open} mountOnEnter unmountOnExit {...props}>
        <Box
          role="dialog"
          aria-modal={isModal}
          aria-label={heading}
          sx={[styles.wrapper, wrapperSx] as SxProps}
          {...otherWrapperProps}>
          <Box sx={styles.handle}>
            <Box sx={styles.handleBar} />
          </Box>

          <Box
            sx={[styles.contentWrapper, contentWrapperSx] as SxProps}
            {...otherContentWrapperProps}>
            <Box
              sx={[styles.heading, headingContainerSx] as SxProps}
              {...otherHeadingContainerProps}>
              {heading && (
                <Box sx={styles.headingBlock}>
                  <Text
                    sx={[styles.headingText, headingTextSx] as SxProps}
                    {...otherHeadingTextProps}>
                    {heading}
                  </Text>
                  {/* A Box, not a Text: the subheading may be a node with blocks of
                      its own, which cannot live inside a paragraph. */}
                  {subheading && <Box sx={styles.subheadingText}>{subheading}</Box>}
                </Box>
              )}

              {closeButton || (
                <IconButton
                  aria-label={t('close')}
                  sx={[styles.closeIcon, iconButtonSx] as SxProps}
                  onClick={onClose}
                  {...otherIconButtonProps}>
                  <Box component={CrossThinIcon} sx={styles.closeGlyph} />
                </IconButton>
              )}
            </Box>
            <Box sx={[styles.mainContent, mainContentSx] as SxProps} {...otherMainContentProps}>
              {children}
            </Box>
          </Box>

          {actionButtonProps && <ActionButtons {...actionButtonProps} />}
        </Box>
      </Slide>
    </>
  )
}

export default memo(SlidePanel)
